<template>
  <div class="container">
    <Background />
    <img class="logo" :src="logo" alt="logo" />
    <Card title="Reset Password" class="card">
      <p v-if="!recoveryAvailable" role="status">
        Please contact Allume support to restore access to your account.
      </p>
      <p v-else-if="completed" role="status">Your password was reset. Please sign in again.</p>
      <p v-else-if="!passwordError?.recoveryRequired">Please enter your new password.</p>
      <p v-if="passwordError" role="alert">{{ passwordError.message }}</p>
      <a-form
        v-if="recoveryAvailable && !completed && !passwordError?.recoveryRequired"
        ref="formRef"
        :model="form"
        layout="vertical"
        :rules="rules"
      >
        <a-form-item label="New password" name="password" :extra="passwordGuidance">
          <a-input-password v-model:value="form.password" size="large" class="input-box" />
        </a-form-item>

        <a-form-item label="Re-enter password" name="passwordConfirm">
          <a-input-password v-model:value="form.passwordConfirm" size="large" class="input-box" />
        </a-form-item>
      </a-form>

      <template #footer>
        <RouterLink
          v-if="!recoveryAvailable || completed || passwordError?.recoveryRequired"
          to="/login"
          >Back to sign in</RouterLink
        >
        <a-button v-else type="primary" :loading="isSaving" @click="resetClicked">Reset</a-button>
      </template>
    </Card>

    <Copyright />
  </div>
</template>

<script>
import Background from '../components/Background.vue';
import Copyright from '../components/Copyright.vue';
import Card from '../components/Card.vue';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { AppContext } from '@/models/AppContext';
import { useAuthStore } from '@/shared/stores/authStore';
import { logout } from '@/shared/auth/session';
import { usePasswordRecovery } from '@/shared/auth/usePasswordRecovery';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { validateCognitoPassword, cognitoPasswordGuidance } from '@/utils/validators';
import { resolveHttpErrorMessage } from '@/http/http-error';

export default {
  name: 'ResetPassword',
  components: { Card, Background, Copyright },
  setup() {
    return { recoveryAvailable: usePasswordRecovery() };
  },
  data() {
    return {
      isSaving: false,
      completed: false,
      passwordError: null,
      passwordGuidance: isCognitoEnabled
        ? cognitoPasswordGuidance
        : 'Use at least six characters, one letter and one number.',
      form: {
        password: '',
        passwordConfirm: '',
      },
      rules: {
        password: [
          { required: true, message: 'Please enter password', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (isCognitoEnabled) return validateCognitoPassword(rule, value);
              if (!/^(?=.*[A-Za-z])(?=.*\d)\S{6,}$/.test(value)) {
                callback(
                  new Error(
                    `Password length must greater than 6 and contain at least one letter and one number`,
                  ),
                );
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        passwordConfirm: [
          {
            required: true,
            message: 'Please input password again',
            trigger: 'blur',
          },
          {
            validator: (rule, value, callback) => {
              if (value !== this.form.password) {
                callback(new Error("Two inputs don't match!"));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
      },
    };
  },
  methods: {
    async resetClicked() {
      if (
        !this.recoveryAvailable ||
        this.isSaving ||
        this.completed ||
        this.passwordError?.recoveryRequired
      )
        return;
      this.isSaving = true;
      try {
        try {
          await this.$refs.formRef?.validate();
        } catch {
          return;
        }
        this.passwordError = null;
        const sessionVersion = cognitoClient.sessionVersion();
        await this.$http.post(this.$api.resetPassword, {
          password: this.form.password,
          token: this.$route.params.token,
        });
        this.completed = true;
        this.form.password = '';
        this.form.passwordConfirm = '';
        this.$message.success('password reset successfully.');
        if (isNativeCognitoEnabled && sessionVersion !== cognitoClient.sessionVersion()) return;
        try {
          await logout(AppContext.SolCentre);
        } catch {
          this.$message.error(
            'Your password was reset, but sign-out did not complete. Close this session and sign in again.',
          );
        }
      } catch (e) {
        this.passwordError = {
          message: resolveHttpErrorMessage(e, 'Password reset could not be confirmed.'),
          recoveryRequired: e?.recoveryRequired === true,
        };
        if (this.passwordError.recoveryRequired) {
          this.form.password = '';
          this.form.passwordConfirm = '';
        }
        this.$message.error(this.passwordError.message);
      } finally {
        this.isSaving = false;
      }
    },
  },
  computed: {
    logo() {
      return useAuthStore().displayLogo;
    },
  },
};
</script>

<style lang="less" scoped>
.container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .logo {
    height: 65px;
    display: block;
    margin: 100px auto 0;
  }

  .card {
    width: 600px;
    margin: 0 auto;
  }
}
</style>
